import { provide, ref, computed, type ComputedRef } from "vue";
import type { TOptionsBase, i18n } from '../../i18next.js';
import { useTranslation as useTranslationOrig } from "../../i18next.js";
import {
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord,
  injectRequiredWithSelf
} from "@mcwd/util-vue";
import { useFormLogger } from "./use-form-logger.js";
import { isObject, isString, removeEmptyProperties, type PartialWithUndefined } from '@mcwd/typescript-type-guards';

export type TranslateOptions = PartialWithUndefined<TOptionsBase>;
export type TranslateFunction = (key: string, options?: TranslateOptions | string | undefined) => string;

type UseTranslationInjectionValues = InjectionValuesRecord<{
  i18next: i18n;
  translate: TranslateFunction;
  getNamespaceLoaded: (ns: string) => ComputedRef<boolean>;
}>;

type UseTranslationInjectionKeys = InjectionKeysRecord<UseTranslationInjectionValues>;
const UseTranslationInjectionKeys = CreateInjectionKeysRecord<UseTranslationInjectionKeys>({
  i18next: "i18next",
  translate: "translate",
  getNamespaceLoaded: "getNamespaceLoaded"
});

export function initUseTranslation() {
  if (checkValuesAlreadyProvided(UseTranslationInjectionKeys)) {
    return;
  }
  const { i18next, t } = useTranslationOrig();
  const translate: TranslateFunction = (key: string, options?: TranslateOptions | string | undefined) => {
    let opts = options == null ? undefined : (isString(options) ? options : removeEmptyProperties<TranslateOptions>(options));
    opts = isObject(opts) && Object.keys(opts).length === 0 ? undefined : opts;
    const res = t(key, opts);
    return res;
  };
  function checkNamespaceLoaded(ns: string) {
    return i18next.isInitialized && i18next.hasLoadedNamespace(ns);
  }
  function onNamespaceLoaded(ns: string, callbackFn: () => void, timeout: number){
    if (checkNamespaceLoaded(ns)){
      callbackFn();
    }
    else {
      setTimeout(() => {
        onNamespaceLoaded(ns, callbackFn, timeout);
      }, timeout);
    }
  }
  const getNamespaceLoaded = (ns: string): ComputedRef<boolean> => {
    const namespaceLoaded = ref(false);
    onNamespaceLoaded(ns, () => {
      namespaceLoaded.value = true;
    }, 1000);
    return computed(() => namespaceLoaded.value);
  };
  provide(UseTranslationInjectionKeys.getNamespaceLoaded, getNamespaceLoaded);
  provide(UseTranslationInjectionKeys.i18next, i18next);
  provide(UseTranslationInjectionKeys.translate, (translate as TranslateFunction));
}

export function useTranslation ({ callerLabel }: { callerLabel?: string | undefined; } = {}) {
  const { logger } = useFormLogger();
  try {
    const getNamespaceLoaded = injectRequiredWithSelf(UseTranslationInjectionKeys.getNamespaceLoaded);
    const i18next = injectRequiredWithSelf(UseTranslationInjectionKeys.i18next);
    const translate = injectRequiredWithSelf(UseTranslationInjectionKeys.translate);
    return { getNamespaceLoaded, i18next, translate };
  }
  catch (err) {
    logger.error(`InjectionError from ${ callerLabel ?? 'unknown' }`);
    throw err;
  }
}